import React, { useMemo, useEffect, useCallback } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { useTheme } from "@/hooks/useTheme";
import { UserType } from "@/types/db/users";
import { ClientType } from "@/types/db/clients";
import { useEntityEdit } from "@/contexts/EntityEditContext";
import { Table, ColumnDef } from "@/components/NewTable";
import { useModal } from "@/hooks/useModal";
import { UserPicker } from "@/components/Pickers/UserPicker";
import { ModalCard } from "@/components/Modals/ModalCard";

interface BannedUserType {
  user: {
    id: string;
    first_name?: string;
    last_name?: string;
    preferred_name?: string;
    email_address?: string;
  };
  reason?: string;
}

export interface RelatedBannedUsersScreenProps {
  clientId: string;
  loading?: boolean;
  externalAddTrigger?: number;
}

export function RelatedBannedUsersScreen({
  clientId,
  loading = false,
  externalAddTrigger,
}: RelatedBannedUsersScreenProps) {
  const { colors } = useTheme();
  const { entityData: client, formData, updateFormData } = useEntityEdit<ClientType>();
  const { openModal, closeModal } = useModal();

  // Use formData if available, otherwise use entityData
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const bannedUsers: BannedUserType[] =
    ((formData as any)?.client_banned_users ?? (client as any)?.client_banned_users) || [];

  const bannedUserIds = useMemo(() => bannedUsers.map((bu) => bu.user.id), [bannedUsers]);

  const handleAddBannedUser = useCallback(
    (user: UserType) => {
      const newBannedUser: BannedUserType = {
        user: {
          id: user.id,
          first_name: user.first_name || undefined,
          last_name: user.last_name || undefined,
          preferred_name: user.preferred_name || undefined,
          email_address: user.email_address || undefined,
        },
        reason: "",
      };
      const updatedBannedUsers = [...bannedUsers, newBannedUser];
      updateFormData({ ...formData, client_banned_users: updatedBannedUsers } as any);
      closeModal();
    },
    [bannedUsers, formData, updateFormData, closeModal],
  );

  const openUserPicker = useCallback(() => {
    openModal(
      <ModalCard>
        <UserPicker
          title="Add Banned User"
          excludeUserIds={bannedUserIds}
          onPick={handleAddBannedUser}
          onCancel={closeModal}
        />
      </ModalCard>,
    );
  }, [openModal, closeModal, bannedUserIds, handleAddBannedUser]);

  // Handle external add trigger from header button
  useEffect(() => {
    if (externalAddTrigger && externalAddTrigger > 0) {
      openUserPicker();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalAddTrigger]);

  const handleRemoveBannedUser = useCallback(
    (row: BannedUserType) => {
      const updatedBannedUsers = bannedUsers.filter((bu) => bu.user.id !== row.user.id);
      updateFormData({ ...formData, client_banned_users: updatedBannedUsers } as any);
    },
    [bannedUsers, formData, updateFormData],
  );

  const handleEdit = useCallback(
    (row: BannedUserType, key: string, newValue: string) => {
      const updated = bannedUsers.map((bu) => (bu.user.id === row.user.id ? { ...bu, [key]: newValue } : bu));
      updateFormData({ ...formData, client_banned_users: updated } as any);
    },
    [bannedUsers, formData, updateFormData],
  );

  const formatUserName = (user: BannedUserType["user"]) => {
    if (!user) return "Unknown User";
    const firstName = user.preferred_name || user.first_name || "";
    const lastName = user.last_name || "";
    return `${firstName} ${lastName}`.trim() || "Unknown User";
  };

  // Define columns
  const columns: ColumnDef<BannedUserType>[] = useMemo(
    () => [
      {
        key: "user",
        header: "User",
        flex: 1,
        minWidth: 150,
        accessor: (row) => formatUserName(row.user),
      },
      {
        key: "email",
        header: "Email",
        flex: 1,
        minWidth: 180,
        accessor: (row) => row.user?.email_address || "—",
      },
      {
        key: "reason",
        header: "Reason",
        flex: 1,
        minWidth: 200,
        editable: { type: "text", placeholder: "Reason for ban" },
        accessor: (row) => row.reason || "",
      },
    ],
    [],
  );

  return (
    <ScrollView
      horizontal={true}
      showsHorizontalScrollIndicator={true}
      contentContainerStyle={{ width: "auto", flexGrow: 1 }}
      style={[styles.container, { backgroundColor: colors.background }]}
    >
      <Table<BannedUserType>
        data={bannedUsers}
        columns={columns}
        keyExtractor={(item) => item.user.id}
        onEdit={handleEdit}
        canEdit
        onDelete={handleRemoveBannedUser}
        canDelete
        emptyMessage="No banned users"
        emptyIcon="account-off"
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
